import { useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, Minus } from 'lucide-react';
import { formatCurrency, cn } from '@/lib/utils';

export const DASHBOARD_SURFACE_CLASSNAME = [
  'rounded-2xl border border-white/20 backdrop-blur-xl',
  'bg-white/80 dark:bg-slate-900/80',
  'shadow-xl shadow-slate-200/50 dark:shadow-slate-900/50',
  'transition-all duration-300 hover:shadow-2xl hover:scale-[1.01]',
].join(' ');

const AnimatedMetricNumber = ({ value, duration = 1000, format = 'number', prefix = '', suffix = '' }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const previousValueRef = useRef(0);

  useEffect(() => {
    let startTime;
    let animationFrame;
    const startValue = previousValueRef.current;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const nextValue = startValue + (value - startValue) * easeOut;
      setDisplayValue(nextValue);

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        previousValueRef.current = value;
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);

  const formatted = format === 'currency'
    ? formatCurrency(displayValue)
    : format === 'percent'
      ? `${Math.round(displayValue)}%`
      : Math.round(displayValue).toLocaleString('pt-PT');

  return <span>{prefix}{formatted}{suffix}</span>;
};

export const DashboardStatCard = ({
  title,
  value,
  change,
  icon: Icon,
  color = 'blue',
  format = 'currency',
  subtitle,
  emphasis = 'secondary',
  onClick,
  testId,
}) => {
  const isPositive = Number(change) > 0;
  const isNegative = Number(change) < 0;

  const colorClasses = {
    blue: 'from-blue-500 to-indigo-600',
    green: 'from-emerald-500 to-teal-600',
    purple: 'from-purple-500 to-violet-600',
    orange: 'from-orange-500 to-amber-600',
    red: 'from-red-500 to-rose-600',
    pink: 'from-pink-500 to-rose-600',
    cyan: 'from-cyan-500 to-blue-600',
  };

  return (
    <div
      className={cn(
        DASHBOARD_SURFACE_CLASSNAME,
        emphasis === 'primary' ? 'p-6' : 'p-5',
        'group cursor-pointer',
        onClick && 'hover:border-blue-300'
      )}
      onClick={onClick}
      data-testid={testId || `dashboard-stat-card-${String(title || 'stat').toLowerCase().replace(/\s/g, '-')}`}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      } : undefined}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className={cn(emphasis === 'primary' ? 'mb-2 text-base font-medium text-slate-500' : 'mb-1 text-sm font-medium text-slate-500')}>
            {title}
          </p>
          <div className={cn(emphasis === 'primary' ? 'text-3xl font-bold text-slate-950' : 'text-2xl font-bold text-slate-900')}>
            <AnimatedMetricNumber value={Number(value || 0)} format={format} duration={1200} />
          </div>

          {(change !== undefined && change !== null) && (
            <div className="mt-2 flex items-center gap-1.5">
              <div className={cn(
                'flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-xs font-medium',
                isPositive && 'bg-emerald-100 text-emerald-700',
                isNegative && 'bg-red-100 text-red-700',
                !isPositive && !isNegative && 'bg-slate-100 text-slate-600'
              )}>
                {isPositive && <ArrowUp className="h-3 w-3" />}
                {isNegative && <ArrowDown className="h-3 w-3" />}
                {!isPositive && !isNegative && <Minus className="h-3 w-3" />}
                <span>{Math.abs(Number(change || 0)).toFixed(1)}%</span>
              </div>
              {subtitle ? <span className="text-xs text-slate-400">{subtitle}</span> : null}
            </div>
          )}

          {!subtitle && (change === undefined || change === null) ? <div className="mt-2 h-[18px]" /> : null}
          {subtitle && (change === undefined || change === null) ? <p className="mt-2 text-xs text-slate-400">{subtitle}</p> : null}
        </div>

        <div className={cn(
          emphasis === 'primary' ? 'rounded-2xl bg-gradient-to-br p-3.5 shadow-lg' : 'rounded-xl bg-gradient-to-br p-3 shadow-lg',
          'transition-transform duration-300 group-hover:scale-110',
          colorClasses[color] || colorClasses.blue
        )}>
          <Icon className={cn(emphasis === 'primary' ? 'h-[22px] w-[22px] text-white' : 'h-5 w-5 text-white')} />
        </div>
      </div>
    </div>
  );
};